import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Clock, LogOut, Users, Bell, Search as SearchIcon, BarChart3, Baby, Phone,
  Pause, Play, Check, Loader2, BadgePercent, Wallet, CreditCard, Trash2, Undo2, Calendar, StickyNote, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import {
  getPending, getActive, getStats, confirmCheckin, updatePayment,
  checkoutChild, checkoutGroup, searchParents, pauseVisit, resumeVisit,
  getDailyStats, deletePending, refundPayment, markNoShow, collectVisitPayment, markVisitNoShow, requestPaymentChange, apiError,
} from "../lib/api";
import { fmtTime, fmtElapsed, calcAgeMonths, formatAge, formatAgeHost, isUnder24Months } from "../lib/utils";
import { BrandMark } from "../components/Brand";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const AMOUNTS = [350, 500, 700, 1050];

function ManagePaymentDialog({ visit, onClose, onSubmit, t }) {
  const [mode, setMode] = useState(null); // null | 'amount' | 'type' | 'refund'
  const [amount, setAmount] = useState("");
  const [ptype, setPtype] = useState("card");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (visit) { setMode(null); setAmount(String(visit.payment_amount ?? "")); setPtype(visit.payment_type || "card"); }
  }, [visit]);
  if (!visit) return null;
  const send = async (payload) => {
    setBusy(true);
    try { await onSubmit(payload); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!visit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl border-2 border-[#26422A]/40 bg-[#FFFFFF]" data-testid="manage-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#26422A]">{t("managePayment")}</DialogTitle>
        </DialogHeader>
        <div className="rounded-xl bg-[#FFEEBC] p-3 text-sm font-bold text-[#26422A]">
          {visit.child_name} · ₺{visit.payment_amount} · {t(visit.payment_type || "other")}
        </div>
        {mode === null && (
          <div className="flex flex-col gap-2">
            <Button data-testid="mp-change-amount" variant="outline" onClick={() => setMode("amount")} className="h-11 w-full rounded-xl border-[#26422A] font-bold text-[#26422A]">{t("changeAmount")}</Button>
            <Button data-testid="mp-change-type" variant="outline" onClick={() => setMode("type")} className="h-11 w-full rounded-xl border-[#26422A] font-bold text-[#26422A]">{t("changeType")}</Button>
            <Button data-testid="mp-refund" onClick={() => setMode("refund")} className="h-11 w-full rounded-xl bg-[#5D372A] font-bold text-white hover:opacity-90">{t("refundFull")}</Button>
          </div>
        )}
        {mode === "amount" && (
          <div className="space-y-3">
            <Label className="font-bold text-[#26422A]">{t("newAmount")} (₺)</Label>
            <Input data-testid="mp-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 rounded-xl border-[#26422A]/60 font-semibold" />
            <Button data-testid="mp-submit" disabled={busy || !amount} onClick={() => send({ action: "amount", new_amount: parseFloat(amount) })} className="h-11 w-full rounded-xl bg-[#EA672D] font-bold text-white">{t("saveChanges")}</Button>
          </div>
        )}
        {mode === "type" && (
          <div className="space-y-3">
            <div className="flex gap-2">
              {["card", "cash"].map((pt) => (
                <button key={pt} data-testid={`mp-type-${pt}`} onClick={() => setPtype(pt)} className={`h-11 flex-1 rounded-xl border-2 font-bold ${ptype === pt ? "border-[#26422A] bg-[#26422A] text-[#FFEEBC]" : "border-[#26422A]/40 text-[#26422A]"}`}>{t(pt)}</button>
              ))}
            </div>
            <Button data-testid="mp-submit" disabled={busy} onClick={() => send({ action: "type", new_type: ptype })} className="h-11 w-full rounded-xl bg-[#EA672D] font-bold text-white">{t("saveChanges")}</Button>
          </div>
        )}
        {mode === "refund" && (
          <div className="space-y-3">
            <p className="text-sm text-[#26422A]">{t("confirmRefundBody")}</p>
            <Button data-testid="mp-submit" disabled={busy} onClick={() => send({ action: "refund" })} className="h-11 w-full rounded-xl bg-[#5D372A] font-bold text-white">{t("refundFull")}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ open, onClose, group, onSubmit, onNoShow, t }) {
  const [amount, setAmount] = useState(null);
  const [custom, setCustom] = useState("");
  const [type, setType] = useState("cash");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setAmount(null); setCustom(""); setType("card"); setNotes(""); }
  }, [open]);

  const effAmount = custom ? parseFloat(custom) : amount;
  const isLoyalty = type === "loyalty_discount";
  const hasAmount = effAmount != null && !isNaN(effAmount);

  const submit = async (skip) => {
    setBusy(true);
    try {
      const note = notes.trim() || null;
      const amountToSend = hasAmount ? effAmount : (isLoyalty ? 0 : effAmount);
      await onSubmit(
        skip
          ? { skip_payment: true, notes: note }
          : { payment_amount: amountToSend, payment_type: type, skip_payment: false, notes: note }
      );
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const types = [
    { k: "card", label: t("card"), icon: CreditCard },
    { k: "cash", label: t("cash"), icon: Wallet },
    { k: "loyalty_discount", label: t("loyaltyDiscount"), icon: BadgePercent },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl border-2 border-[#26422A]/40 bg-[#FFFFFF]" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#26422A]">
            {group?.mode === "collect" ? t("collectPayment") : t("confirmPaymentCheckin")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <p className="mb-2 text-sm font-bold text-[#26422A]">{t("paymentAmount")}</p>
            <div className="grid grid-cols-2 gap-2">
              {AMOUNTS.map((a) => (
                <button
                  key={a}
                  data-testid={`amount-${a}`}
                  onClick={() => { setAmount(a); setCustom(""); }}
                  className={`rounded-xl border-2 py-3 text-base font-bold transition-colors ${
                    amount === a && !custom
                      ? "border-[#26422A] bg-[#26422A] text-[#FFEEBC]"
                      : "border-[#26422A]/50 bg-[#FFEEBC] text-[#26422A]"
                  }`}
                >
                  ₺{a}
                </button>
              ))}
            </div>
            <Input
              data-testid="custom-amount-input"
              type="number"
              value={custom}
              onChange={(e) => { setCustom(e.target.value); setAmount(null); }}
              placeholder={t("otherAmount")}
              className="mt-2 h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
            />
          </div>
          <div>
            <p className="mb-2 text-sm font-bold text-[#26422A]">{t("paymentType")}</p>
            <div className="grid grid-cols-3 gap-2">
              {types.map(({ k, label, icon: Icon }) => (
                <button
                  key={k}
                  data-testid={`ptype-${k}`}
                  onClick={() => setType(k)}
                  className={`flex flex-col items-center gap-1 rounded-xl border-2 py-3 text-sm font-bold transition-colors ${
                    type === k
                      ? "border-[#26422A] bg-[#26422A] text-[#FFEEBC]"
                      : "border-[#26422A]/50 bg-[#FFEEBC] text-[#26422A]"
                  }`}
                >
                  <Icon className="h-5 w-5" /> {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-sm font-bold text-[#26422A]">{t("notes")}</p>
            <Textarea
              data-testid="payment-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("notesPlaceholder")}
              rows={2}
              className="rounded-xl border-[#26422A]/60 bg-white font-medium"
            />
          </div>
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button
            data-testid="confirm-payment-btn"
            onClick={() => submit(false)}
            disabled={busy || (!hasAmount && !isLoyalty)}
            className="h-12 w-full rounded-xl bg-[#26422A] font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> :
              (group?.mode === "collect" ? t("paymentCollected") : t("confirmWithPayment"))}
          </Button>
          {group?.mode !== "collect" && (
            <Button
              data-testid="skip-payment-btn"
              variant="outline"
              onClick={() => submit(true)}
              disabled={busy}
              className="h-11 w-full rounded-xl border-[#26422A] font-bold text-[#26422A]"
            >
              {t("skipPayment")}
            </Button>
          )}
          {group?.mode === "collect" && (
            <Button
              data-testid="no-show-btn"
              variant="outline"
              onClick={() => { onClose(); onNoShow?.(group); }}
              disabled={busy}
              className="h-11 w-full rounded-xl border-2 border-[#EA672D] bg-transparent font-bold text-[#EA672D] hover:bg-[#EA672D]/10"
            >
              {t("noShowExit")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function HostDashboard() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { auth, logout } = useAuth();

  const [pending, setPending] = useState([]);
  const [active, setActive] = useState([]);
  const [stats, setStats] = useState({ total_kids_inside: 0, total_pending: 0 });
  const [tab, setTab] = useState("pending");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [daily, setDaily] = useState(null);
  const todayIst = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul" }).format(new Date());
  const [statsDate, setStatsDate] = useState(() =>
    new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul" }).format(new Date())
  );
  const [payGroup, setPayGroup] = useState(null);
  const [manageVisit, setManageVisit] = useState(null);
  const [noShowGroup, setNoShowGroup] = useState(null);
  const [refundGroup, setRefundGroup] = useState(null);
  const [deleteGroup, setDeleteGroup] = useState(null);
  const [youngWarnGroup, setYoungWarnGroup] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(Date.now());
  const acting = useRef(false);

  useEffect(() => {
    if (!auth) navigate("/host/login");
  }, [auth, navigate]);

  const load = useCallback(async () => {
    if (!auth) return;
    try {
      const [p, a, s] = await Promise.all([getPending(auth), getActive(auth), getStats(auth)]);
      setPending(p);
      setActive(a);
      setStats(s);
      setFetchedAt(Date.now());
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        navigate("/host/login");
      }
    }
  }, [auth, logout, navigate]);

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (tab === "stats" && auth) {
      setDaily(null);
      getDailyStats(auth, statsDate).then(setDaily).catch(() => {});
    }
  }, [tab, auth, statsDate]);

  const doSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      setResults(await searchParents(query.trim(), auth));
    } catch (e) {
      toast.error(t("searchFailed"));
    } finally {
      setSearching(false);
    }
  };

  const handleConfirm = async (gid, payment) => {
    await confirmCheckin(gid, auth, payment);
    toast.success(t("confirmPaymentCheckin"));
    load();
  };
  const handleCollect = async (gid, payment) => {
    await updatePayment(gid, auth, payment);
    toast.success(t("paymentCollected"));
    load();
  };
  const handleCollectVisit = async (visitId, payment) => {
    await collectVisitPayment(visitId, auth, payment);
    toast.success(t("paymentCollected"));
    load();
  };
  const handleManageSubmit = async (payload) => {
    try {
      await requestPaymentChange(manageVisit.visit_id, auth, payload);
      toast.success(t("paymentUpdated"));
      setManageVisit(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const handleRefund = async (gid, body) => {
    await refundPayment(gid, auth, body);
    toast.success(t("paymentRefunded"));
    load();
  };
  const handleNoShow = async () => {
    if (!noShowGroup) return;
    try {
      if (typeof noShowGroup === "object" && noShowGroup.kind === "visit") {
        await markVisitNoShow(noShowGroup.id, auth);
      } else {
        await markNoShow(noShowGroup, auth);
      }
      toast.success(t("noShowDone"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setNoShowGroup(null);
    }
  };
  const handleDeletePending = async () => {
    if (!deleteGroup) return;
    try {
      await deletePending(deleteGroup, auth);
      toast.success(t("checkinRemoved"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleteGroup(null);
      load();
    }
  };

  const wrap = async (fn) => {
    if (acting.current) return;
    acting.current = true;
    try { await fn(); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { acting.current = false; }
  };

  // group active by visit_group_id
  const groups = {};
  active.forEach((c) => {
    (groups[c.visit_group_id] = groups[c.visit_group_id] || []).push(c);
  });
  const activeGroups = Object.entries(groups);

  return (
    <div className="min-h-screen bg-[#FFEEBC]">
      <header className="sticky top-0 z-20 border-b border-[#26422A]/30 bg-[#26422A]">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-2 px-4 py-4">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <BrandMark size="sm" />
            <div className="min-w-0">
              <div className="truncate font-display text-lg font-bold text-[#FFEEBC]">{t("dashboard")}</div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <LanguageSwitcher />
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/"); }}
              className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 text-sm font-bold text-[#FFEEBC] hover:bg-white/20"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">{t("logout")}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">
        <div className="mb-6 grid grid-cols-2 gap-4">
          <div className="rounded-2xl border-2 border-[#26422A]/40 bg-white p-5" data-testid="stat-inside">
            <Users className="mb-2 h-6 w-6 text-[#26422A]" />
            <div className="font-display text-3xl font-black text-[#26422A]">{stats.total_kids_inside}</div>
            <div className="text-sm font-bold text-[#26422A]">{t("inside")}</div>
          </div>
          <div className="rounded-2xl border-2 border-[#26422A]/40 bg-[#FFFFFF] p-5" data-testid="stat-pending">
            <Bell className="mb-2 h-6 w-6 text-[#EA672D]" />
            <div className="font-display text-3xl font-black text-[#26422A]">{stats.total_pending}</div>
            <div className="text-sm font-bold text-[#26422A]">{t("pending")}</div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-4 rounded-xl bg-[#FFEEBC] p-1">
            <TabsTrigger data-testid="tab-pending" value="pending" className="rounded-lg font-bold data-[state=active]:bg-[#26422A] data-[state=active]:text-[#FFEEBC]">
              {t("pending")}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-inside" value="inside" className="rounded-lg font-bold data-[state=active]:bg-[#26422A] data-[state=active]:text-[#FFEEBC]">
              {t("inside")}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-search" value="search" className="rounded-lg font-bold data-[state=active]:bg-[#26422A] data-[state=active]:text-[#FFEEBC]">
              {t("search")}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-stats" value="stats" className="rounded-lg font-bold data-[state=active]:bg-[#26422A] data-[state=active]:text-[#FFEEBC]">
              {t("statsTab")}
            </TabsTrigger>
          </TabsList>

          {/* PENDING */}
          <TabsContent value="pending" className="mt-5 space-y-4" data-testid="pending-list">
            {pending.length === 0 ? (
              <EmptyState icon={Check} title={t("noPendingCheckins")} sub={t("allCaughtUp")} />
            ) : (
              <AnimatePresence>
                {pending.map((p) => (
                  <motion.div
                    key={p.visit_group_id}
                    layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="rounded-2xl border-2 border-[#26422A]/40 bg-[#FFFFFF] p-5"
                    data-testid={`pending-card-${p.visit_group_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-display text-lg font-bold text-[#26422A]">
                          {p.parent_name} {p.parent_surname}
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-[#26422A]">
                          <Phone className="h-3.5 w-3.5" /> {p.parent_phone}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-[#EA672D] text-white hover:bg-[#EA672D]">{fmtTime(p.time_submitted)}</Badge>
                        <button
                          data-testid={`delete-pending-btn-${p.visit_group_id}`}
                          onClick={() => setDeleteGroup(p.visit_group_id)}
                          title={t("removeCheckin")}
                          className="flex h-8 w-8 items-center justify-center rounded-lg border-2 border-[#5D372A]/40 text-[#5D372A] transition-colors hover:bg-[#5D372A] hover:text-white"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {p.children.map((c) => {
                        const months = calcAgeMonths(c.birthday);
                        const young = months != null && months <= 24;
                        return (
                          <span
                            key={c.visit_id}
                            data-testid={`pending-child-${c.visit_id}`}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold ${
                              young
                                ? "bg-[#EA672D]/15 text-[#5D372A] ring-1 ring-[#EA672D]/50"
                                : "bg-[#FFEEBC] text-[#26422A]"
                            }`}
                          >
                            {young ? <AlertTriangle className="h-3.5 w-3.5 text-[#EA672D]" /> : <Baby className="h-3.5 w-3.5" />}
                            {c.name}
                            {formatAge(c.birthday, t) && (
                              <span className="font-semibold opacity-70">· {formatAge(c.birthday, t)}</span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                    <Button
                      data-testid={`confirm-btn-${p.visit_group_id}`}
                      onClick={() => {
                        const hasYoung = p.children.some((c) => {
                          const m = calcAgeMonths(c.birthday);
                          return m != null && m <= 24;
                        });
                        if (hasYoung) {
                          setYoungWarnGroup(p.visit_group_id);
                        } else {
                          setPayGroup({ id: p.visit_group_id, mode: "confirm" });
                        }
                      }}
                      className="mt-4 h-12 w-full rounded-xl bg-[#26422A] font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
                    >
                      {t("confirmPaymentCheckin")}
                    </Button>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </TabsContent>

          {/* INSIDE */}
          <TabsContent value="inside" className="mt-5 space-y-3" data-testid="inside-list">
            {active.length === 0 ? (
              <EmptyState icon={Baby} title={t("noKidsInside")} sub={t("playgroundEmpty")} />
            ) : (
              active.map((c) => (
                <div key={c.visit_id} className="rounded-2xl border-2 border-[#26422A]/40 bg-[#FFFFFF] p-3 sm:p-4" data-testid={`inside-card-${c.visit_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#26422A]">
                        <Baby className="h-5 w-5 text-[#FFEEBC]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="break-words font-display text-base font-bold leading-tight text-[#26422A]">{c.child_name}</span>
                          {formatAgeHost(c.child_birthday, t) && (
                            <span
                              data-testid={`active-child-age-${c.visit_id}`}
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${
                                isUnder24Months(c.child_birthday) ? "bg-[#EA672D] text-white" : "bg-[#26422A]/10 text-[#26422A]"
                              }`}
                            >
                              {isUnder24Months(c.child_birthday) && <AlertTriangle className="h-3 w-3" />}
                              {formatAgeHost(c.child_birthday, t)}
                            </span>
                          )}
                          {!c.payment_completed && (
                            <span data-testid={`unpaid-badge-${c.visit_id}`} className="inline-flex items-center rounded-full bg-[#EA672D]/15 px-2 py-0.5 text-xs font-bold text-[#EA672D]">
                              {t("unpaid")}
                            </span>
                          )}
                          {c.pending_change && (
                            <span data-testid={`pending-change-${c.visit_id}`} className="inline-flex items-center rounded-full bg-[#26422A]/10 px-2 py-0.5 text-xs font-bold text-[#26422A]">
                              {t("awaitingApproval")}
                            </span>
                          )}
                        </div>
                        <div className="truncate text-xs text-[#26422A]/70">{c.parent_name} {c.parent_surname}</div>
                        <div className="mt-0.5 flex items-center gap-1.5 text-sm font-semibold text-[#26422A]">
                          <Clock className="h-3.5 w-3.5 shrink-0" />
                          {c.is_paused ? (
                            <span className="text-[#EA672D]">{t("onBreak")}</span>
                          ) : (
                            <LiveTimer elapsedSeconds={c.elapsed_seconds || 0} isPaused={c.is_paused} baseAt={fetchedAt} />
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      data-testid={`pause-btn-${c.visit_id}`}
                      onClick={() => wrap(() => (c.is_paused ? resumeVisit(c.visit_id, auth) : pauseVisit(c.visit_id, auth)))}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-2 border-[#26422A] text-[#26422A] hover:bg-[#FFEEBC]"
                      aria-label={c.is_paused ? t("resume") : t("pause")}
                    >
                      {c.is_paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="mt-3 flex items-stretch gap-2">
                    {c.payment_completed && !c.refunded ? (
                      <button
                        data-testid={`paid-badge-${c.visit_id}`}
                        onClick={() => setManageVisit(c)}
                        className="flex h-11 flex-1 items-center justify-center rounded-xl bg-[#26422A] px-3 text-sm font-bold text-white hover:bg-[#1B3320]"
                      >
                        {t("paid")} · ₺{c.payment_amount}
                      </button>
                    ) : c.refunded ? (
                      <div data-testid={`refunded-badge-${c.visit_id}`} className="flex h-11 flex-1 items-center justify-center rounded-xl bg-[#E5E7EB] px-3 text-sm font-bold text-[#23432D]">
                        {t("refunded")}
                      </div>
                    ) : (
                      <button
                        data-testid={`collect-btn-${c.visit_id}`}
                        onClick={() => setPayGroup({ id: c.visit_id, kind: "visit", mode: "collect" })}
                        className="flex h-11 flex-1 items-center justify-center rounded-xl bg-[#EA672D] px-3 text-sm font-bold text-white hover:bg-[#d4571f]"
                      >
                        {t("collectPayment")}
                      </button>
                    )}
                    <button
                      data-testid={`checkout-btn-${c.visit_id}`}
                      onClick={() => wrap(() => checkoutChild(c.visit_id, auth))}
                      className="flex h-11 flex-1 items-center justify-center rounded-xl bg-[#5D372A] px-3 text-sm font-bold text-white hover:opacity-90"
                    >
                      {t("checkOut")}
                    </button>
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          {/* SEARCH */}
          <TabsContent value="search" className="mt-5" data-testid="search-panel">
            <div className="flex gap-2">
              <Input
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doSearch()}
                placeholder={t("searchByPhoneOrName")}
                className="h-12 rounded-xl border-[#26422A]/60 bg-[#FFFFFF] font-semibold"
              />
              <Button
                data-testid="search-btn"
                onClick={doSearch}
                disabled={searching}
                className="h-12 rounded-xl bg-[#26422A] px-5 font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
              >
                {searching ? <Loader2 className="h-5 w-5 animate-spin" /> : <SearchIcon className="h-5 w-5" />}
              </Button>
            </div>
            <div className="mt-4 space-y-3">
              {results.length === 0 ? (
                <EmptyState icon={SearchIcon} title={t("noResults")} sub={t("tryDifferentSearch")} />
              ) : (
                results.map((r) => (
                  <div key={r.id} className="rounded-2xl border-2 border-[#26422A]/40 bg-[#FFFFFF] p-5" data-testid={`search-result-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-display text-lg font-bold text-[#26422A]">{r.name} {r.surname}</div>
                        <div className="flex items-center gap-1.5 text-sm text-[#26422A]"><Phone className="h-3.5 w-3.5" /> {r.phone_number}</div>
                      </div>
                      <Badge variant="outline" className="border-[#26422A] text-[#26422A]">
                        {r.total_visits} {t("totalVisits")}
                      </Badge>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {r.children.map((c) => (
                        <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full bg-[#FFEEBC] px-3 py-1 text-sm font-bold text-[#26422A]">
                          <Baby className="h-3.5 w-3.5" /> {c.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* STATS */}
          <TabsContent value="stats" className="mt-5" data-testid="stats-panel">
            <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-[#26422A]/40 bg-[#FFFFFF] p-4">
              <div className="flex items-center gap-2 text-sm font-bold text-[#26422A]">
                <Calendar className="h-4 w-4 text-[#26422A]" /> {t("showingStatsFor")}
              </div>
              <Input
                data-testid="stats-date-input"
                type="date"
                value={statsDate}
                max={todayIst}
                onChange={(e) => setStatsDate(e.target.value)}
                className="h-10 w-auto rounded-xl border-[#26422A]/60 bg-white font-semibold"
              />
              {statsDate !== todayIst && (
                <button
                  data-testid="stats-today-btn"
                  onClick={() => setStatsDate(todayIst)}
                  className="rounded-full bg-[#26422A] px-3 py-1.5 text-xs font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
                >
                  {t("today")}
                </button>
              )}
            </div>

            {!daily ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#26422A]" /></div>
            ) : (
              <div className="space-y-5">
                <h2 className="font-display text-lg font-bold text-[#26422A]">
                  {t("summary")} — {daily.selected_date}
                </h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <StatBox label={t("totalCheckins")} value={daily.total_checkins} />
                  <StatBox label={t("totalCheckouts")} value={daily.total_checkouts} />
                  <StatBox label={t("totalRevenue")} value={`₺${daily.total_revenue}`} highlight />
                  <StatBox label={t("avgDuration")} value={`${daily.avg_duration_minutes} ${t("min")}`} />
                </div>
                <div className="rounded-2xl border-2 border-[#26422A]/40 bg-[#FFFFFF] p-5">
                  <h3 className="mb-3 font-bold text-[#26422A]">{t("paymentBreakdown")}</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <PayStat label={t("cash")} value={daily.cash_total} />
                    <PayStat label={t("card")} value={daily.card_total} />
                    <PayStat label={t("otherPayment")} value={daily.other_total} />
                  </div>
                  {daily.unpaid_count > 0 && (
                    <div className="mt-3 rounded-xl bg-[#FFEEBC] p-3 text-sm font-bold text-[#EA672D]">
                      {t("unpaidVisits")}: {daily.unpaid_count}
                    </div>
                  )}
                  {daily.no_show_count > 0 && (
                    <div className="mt-3 rounded-xl bg-[#FFEEBC] p-3 text-sm font-bold text-[#EA672D]" data-testid="stats-no-show">
                      {t("noShowStatus")}: {daily.no_show_count}
                    </div>
                  )}
                  {daily.total_refunds > 0 && (
                    <div className="mt-3 rounded-xl bg-[#FFEEBC] p-3 text-sm font-bold text-[#5D372A]" data-testid="stats-refunds">
                      {t("totalRefunds")}: ₺{daily.total_refunds}
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="mb-3 font-bold text-[#26422A]">{t("childrenDetails")}</h3>
                  {daily.children.length === 0 ? (
                    <p className="text-sm text-[#26422A]">{t("noVisitsToday")}</p>
                  ) : (
                    <div className="space-y-3">
                      {daily.children.map((c, i) => {
                        const paidText = c.no_show || c.payment_type === "no_show"
                          ? t("noShowStatus")
                          : c.refunded
                          ? `${t("refunded")} ₺${c.refund_amount ?? c.payment_amount}`
                          : c.payment_type === "loyalty_discount"
                          ? `${t("unpaid")} (${t("loyaltyDiscount")})`
                          : c.payment_completed && c.payment_amount
                          ? `₺${c.payment_amount} · ${t(c.payment_type || "other")}`
                          : t("unpaid");
                        const timeText = c.checked_in_at
                          ? `${fmtTime(c.checked_in_at)}${c.checked_out_at ? " → " + fmtTime(c.checked_out_at) : ""}`
                          : "--";
                        return (
                          <div
                            key={i}
                            className="rounded-2xl border border-[#26422A]/30 bg-white p-4"
                            data-testid={`stats-child-${i}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="font-display font-bold text-[#26422A]">{c.child_name}</div>
                                <div className="text-xs text-[#26422A]">{c.parent_name}</div>
                              </div>
                              <StatusBadge status={c.status} t={t} />
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                              <Detail label={t("duration")} value={c.elapsed_minutes != null ? fmtElapsed(c.elapsed_minutes, t) : "--"} />
                              <Detail label={t("paid")} value={paidText} />
                              <Detail label={t("phoneNumber")} value={c.parent_phone || "--"} />
                              <Detail label={t("checkedInAt")} value={timeText} />
                            </div>
                            {(c.notes || c.refund_notes) && (
                              <div className="mt-2 rounded-lg bg-[#FFEEBC] p-2.5 text-xs font-medium text-[#5D372A]" data-testid={`stats-note-${i}`}>
                                {c.notes && (
                                  <div className="flex items-start gap-1.5">
                                    <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {c.notes}
                                  </div>
                                )}
                                {c.refund_notes && (
                                  <div className="mt-1 flex items-start gap-1.5">
                                    <Undo2 className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {c.refund_notes}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <PaymentDialog
        open={!!payGroup}
        group={payGroup}
        onClose={() => setPayGroup(null)}
        onSubmit={(payment) =>
          payGroup.kind === "visit"
            ? handleCollectVisit(payGroup.id, payment)
            : payGroup.mode === "collect"
            ? handleCollect(payGroup.id, payment)
            : handleConfirm(payGroup.id, payment)
        }
        onNoShow={(g) => setNoShowGroup(g.kind === "visit" ? { id: g.id, kind: "visit" } : g.id)}
        t={t}
      />

      <ManagePaymentDialog
        visit={manageVisit}
        onClose={() => setManageVisit(null)}
        onSubmit={handleManageSubmit}
        t={t}
      />

      <AlertDialog open={!!noShowGroup} onOpenChange={(o) => !o && setNoShowGroup(null)}>
        <AlertDialogContent className="rounded-3xl border-2 border-[#EA672D]/50 bg-[#FFFFFF]" data-testid="no-show-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl text-[#26422A]">
              {t("noShowConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#26422A]">
              {t("noShowConfirmBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded-xl border-[#26422A]/40 font-bold text-[#26422A]">
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-no-show-btn"
              onClick={handleNoShow}
              className="rounded-xl bg-[#EA672D] font-bold text-white hover:bg-[#d4571f]"
            >
              {t("noShowConfirmAction")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RefundDialog
        open={!!refundGroup}
        group={refundGroup}
        onClose={() => setRefundGroup(null)}
        onSubmit={(body) => handleRefund(refundGroup.id, body)}
        t={t}
      />

      <AlertDialog open={!!deleteGroup} onOpenChange={(o) => !o && setDeleteGroup(null)}>
        <AlertDialogContent className="rounded-3xl border-2 border-[#26422A]/40 bg-[#FFFFFF]" data-testid="delete-pending-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl text-[#26422A]">
              {t("removeCheckin")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#26422A]">
              {t("removeCheckinConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded-xl border-[#26422A] font-bold text-[#26422A]">
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-pending-btn"
              onClick={handleDeletePending}
              className="rounded-xl bg-[#5D372A] font-bold text-white hover:bg-[#4A2B20]"
            >
              {t("remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!youngWarnGroup} onOpenChange={(o) => !o && setYoungWarnGroup(null)}>
        <AlertDialogContent className="rounded-3xl border-2 border-[#EA672D]/50 bg-[#FFFFFF]" data-testid="young-kid-warning-dialog">
          <AlertDialogHeader>
            <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EA672D]/15">
              <AlertTriangle className="h-6 w-6 text-[#EA672D]" />
            </div>
            <AlertDialogTitle className="font-display text-xl text-[#26422A]">
              {t("youngKidWarningTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#26422A]">
              {t("youngKidWarning")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded-xl border-[#26422A]/40 font-bold text-[#26422A]">
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="young-kid-continue-btn"
              onClick={() => {
                setPayGroup({ id: youngWarnGroup, mode: "confirm" });
                setYoungWarnGroup(null);
              }}
              className="rounded-xl bg-[#26422A] font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
            >
              {t("continue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RefundDialog({ open, onClose, group, onSubmit, t }) {
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && group) { setAmount(String(group.amount ?? "")); setNotes(""); }
  }, [open, group]);

  const submit = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return toast.error(t("invalidRefund"));
    setBusy(true);
    try {
      await onSubmit({ refund_amount: val, notes: notes.trim() || null });
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl border-2 border-[#26422A]/40 bg-[#FFFFFF]" data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#26422A]">
            {t("refundPayment")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-xl bg-[#FFEEBC] p-4 text-sm font-bold text-[#26422A]">
            {t("paid")}: ₺{group?.amount} · {t(group?.type || "other")}
          </div>
          <div className="space-y-2">
            <Label className="font-bold text-[#26422A]">{t("refundAmount")}</Label>
            <Input
              data-testid="refund-amount-input"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
            />
            <p className="text-xs text-[#26422A]">{t("refundHint")}</p>
          </div>
          <div className="space-y-2">
            <Label className="font-bold text-[#26422A]">{t("notes")}</Label>
            <Textarea
              data-testid="refund-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("notesPlaceholder")}
              rows={2}
              className="rounded-xl border-[#26422A]/60 bg-white font-medium"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-xl border-[#26422A] font-bold text-[#26422A]"
          >
            {t("cancel")}
          </Button>
          <Button
            data-testid="confirm-refund-btn"
            onClick={submit}
            disabled={busy}
            className="rounded-xl bg-[#5D372A] font-bold text-white hover:bg-[#4A2B20]"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : `${t("refund")} ₺${amount || 0}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EmptyState = ({ icon: Icon, title, sub }) => (
  <div className="flex flex-col items-center rounded-2xl border-2 border-dashed border-[#26422A]/40 py-14 text-center">
    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFEEBC]">
      <Icon className="h-7 w-7 text-[#26422A]" />
    </div>
    <div className="mt-4 font-display text-lg font-bold text-[#26422A]">{title}</div>
    <div className="text-sm text-[#26422A]">{sub}</div>
  </div>
);

const StatBox = ({ label, value, highlight }) => (
  <div className={`rounded-2xl border-2 p-4 ${highlight ? "border-[#26422A] bg-[#26422A]" : "border-[#26422A]/40 bg-[#FFFFFF]"}`}>
    <div className={`font-display text-2xl font-black ${highlight ? "text-[#FFEEBC]" : "text-[#26422A]"}`}>{value}</div>
    <div className={`text-xs font-bold ${highlight ? "text-[#FFEEBC]/70" : "text-[#26422A]"}`}>{label}</div>
  </div>
);

const PayStat = ({ label, value }) => (
  <div className="rounded-xl bg-[#FFEEBC] p-3 text-center">
    <div className="font-display text-xl font-black text-[#26422A]">₺{value}</div>
    <div className="text-xs font-bold text-[#26422A]">{label}</div>
  </div>
);

const Detail = ({ label, value }) => (
  <div className="rounded-lg border border-[#26422A]/10 bg-[#FFEEBC] p-2">
    <div className="text-[10px] font-bold uppercase tracking-wide text-[#26422A]/70">{label}</div>
    <div className="break-words font-bold text-[#26422A]">{value}</div>
  </div>
);

const StatusBadge = ({ status, t }) => {
  const map = {
    active: { c: "bg-[#26422A] text-white", l: t("statusActive") },
    checked_out: { c: "bg-[#5D372A] text-white", l: t("statusCheckedOut") },
    pending: { c: "bg-[#EA672D] text-white", l: t("statusPending") },
    paused: { c: "bg-[#EA672D] text-white", l: t("statusPaused") },
  };
  const s = map[status] || map.pending;
  return <span className={`rounded-full px-3 py-1 text-xs font-bold ${s.c}`}>{s.l}</span>;
};

const LiveTimer = ({ elapsedSeconds, isPaused, baseAt }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isPaused]);
  const extra = isPaused ? 0 : Math.max(0, Math.floor((now - baseAt) / 1000));
  const secs = Math.max(0, elapsedSeconds + extra);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const text = h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  return <span className="font-bold tabular-nums text-[#26422A]" data-testid="live-timer">{text}</span>;
};
